import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown } from "lucide-react";

const HomePage = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const brandStatements = [
    "Some rooms deserve more than background music.",
    "Timeless sound. Intentional performance.",
    "Where live music becomes atmosphere.",
    "Crafted live music for elevated gatherings.",
  ];

  const [currentStatement, setCurrentStatement] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentStatement((prev) => (prev + 1) % brandStatements.length);
    }, 6000);
    return () => clearInterval(interval);
  }, [brandStatements.length]);

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden" data-testid="hero-section">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1621608004563-c5f43776067c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwzfHxhY291c3RpYyUyMGd1aXRhciUyMG11c2ljaWFuJTIwbW9vZHklMjBkYXJrJTIwYW1iaWFuY2V8ZW58MHx8fHwxNzY5MzI1NDM4fDA&ixlib=rb-4.1.0&q=85"
            alt="Live music ambiance"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 hero-gradient" />
        </div>

        {/* Hero Content */}
        <div className="relative z-10 max-w-5xl mx-auto px-6 text-center">
          <div
            className={`transition-all duration-1000 ${
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
            }`}
          >
            <p className="text-xs tracking-[0.3em] uppercase text-[#B8A369] mb-8 animate-fade-in">
              Live Music Experience
            </p>
            
            <h1 className="font-serif text-4xl md:text-6xl lg:text-7xl text-[#F4F2EE] leading-tight mb-8" data-testid="hero-headline">
              Refined Live Country & Soul
              <br />
              <span className="text-3xl md:text-5xl lg:text-6xl font-light text-[#F4F2EE]/80">
                For Private Events & Elevated Gatherings
              </span>
            </h1>

            <div className="line-gold w-24 mx-auto mb-8" />

            <p className="max-w-2xl mx-auto text-base md:text-lg text-[#F4F2EE]/70 leading-relaxed mb-12 animate-fade-in animate-delay-200" data-testid="hero-subheadline">
              A curated live music experience rooted in timeless American sound—designed for discerning hosts and professionally produced events.
            </p>

            <Link
              to="/inquiry"
              className="btn-luxury animate-fade-in animate-delay-300"
              data-testid="hero-cta"
            >
              Request Availability
            </Link>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-12 left-1/2 transform -translate-x-1/2 animate-bounce">
          <ChevronDown size={24} className="text-[#B8A369]/60" />
        </div>
      </section>

      {/* Brand Statement */}
      <section className="py-20 px-6 bg-[#0A0A0A]" data-testid="brand-statement-section">
        <div className="max-w-4xl mx-auto text-center">
          <p className="font-serif text-2xl md:text-3xl lg:text-4xl text-[#F4F2EE]/90 italic leading-relaxed transition-opacity duration-500" data-testid="rotating-statement">
            "{brandStatements[currentStatement]}"
          </p>
        </div>
      </section>

      {/* Experience Section */}
      <section className="py-24 md:py-32 px-6 lg:px-12" data-testid="experience-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Image */}
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1585146720919-9d872c3184c2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwxfHxhY291c3RpYyUyMGd1aXRhciUyMG11c2ljaWFuJTIwbW9vZHklMjBkYXJrJTIwYW1iaWFuY2V8ZW58MHx8fHwxNzY5MzI1NDM4fDA&ixlib=rb-4.1.0&q=85"
                alt="Curated performance"
                className="w-full aspect-[4/5] object-cover opacity-80"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0F0F0F] via-transparent to-transparent" />
            </div>

            {/* Content */}
            <div>
              <p className="text-xs tracking-[0.3em] uppercase text-[#B8A369] mb-4">
                The Experience
              </p>
              <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-[#F4F2EE] mb-8" data-testid="experience-heading">
                Beyond Performance.
                <br />
                <span className="text-[#F4F2EE]/70">Into Atmosphere.</span>
              </h2>

              <div className="space-y-6 text-[#F4F2EE]/70 leading-relaxed">
                <p>
                  This is not simply live music—it is an intentional contribution to your event's atmosphere. Every element, from repertoire selection to volume calibration, is designed to elevate rather than intrude.
                </p>
              </div>

              <div className="line-gold-left w-16 my-10" />

              <div className="space-y-5">
                {[
                  "Curated repertoire tailored to your event's character",
                  "Refined volume control and pacing throughout",
                  "Professional on-site presence and presentation",
                  "Seamless collaboration with planners and producers",
                  "Atmosphere-first performance philosophy",
                ].map((item, index) => (
                  <div key={index} className="flex items-start space-x-4">
                    <span className="text-[#B8A369] mt-1.5">—</span>
                    <p className="text-[#F4F2EE]/70 text-sm">{item}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Ideal Events Section */}
      <section className="py-24 md:py-32 px-6 lg:px-12 bg-[#0A0A0A]" data-testid="ideal-events-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-xs tracking-[0.3em] uppercase text-[#B8A369] mb-4">
              Ideal Settings
            </p>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-[#F4F2EE] mb-6" data-testid="ideal-events-heading">
              Where Atmosphere Matters
            </h2>
            <div className="line-gold w-24 mx-auto" />
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
            {[
              {
                title: "Upscale Weddings",
                description: "Private estates and elevated ceremonies",
                image: "https://images.unsplash.com/photo-1751257576155-9f25d26fb6ef?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHwzfHxlbGVnYW50JTIwd2VkZGluZyUyMHJlY2VwdGlvbiUyMHZlbnVlJTIwZGltbHklMjBsaXR8ZW58MHx8fHwxNzY5MzI1NDQwfDA&ixlib=rb-4.1.0&q=85",
              },
              {
                title: "Corporate Gatherings",
                description: "Executive events and galas",
                image: "https://images.unsplash.com/photo-1768508947362-bca7a379e62a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwzfHxjb3Jwb3JhdGUlMjBnYWxhJTIwZXZlbnQlMjBlbGVnYW50JTIwYXRtb3NwaGVyZXxlbnwwfHx8fDE3NjkzMjU0NDN8MA&ixlib=rb-4.1.0&q=85",
              },
              {
                title: "Boutique Venues",
                description: "Curated showcases and intimate settings",
                image: "https://images.unsplash.com/photo-1694437793118-aa65d7a0b01c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHwyfHxlbGVnYW50JTIwd2VkZGluZyUyMHJlY2VwdGlvbiUyMHZlbnVlJTIwZGltbHklMjBsaXR8ZW58MHx8fHwxNzY5MzI1NDQwfDA&ixlib=rb-4.1.0&q=85",
              },
              {
                title: "Private Events",
                description: "Invitation-only and referral-based",
                image: "https://images.unsplash.com/photo-1653821355736-0c2598d0a63e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwyfHxjb3Jwb3JhdGUlMjBnYWxhJTIwZXZlbnQlMjBlbGVnYW50JTIwYXRtb3NwaGVyZXxlbnwwfHx8fDE3NjkzMjU0NDN8MA&ixlib=rb-4.1.0&q=85",
              },
            ].map((event, index) => (
              <div key={index} className="group" data-testid={`event-card-${index}`}>
                <div className="relative aspect-[3/4] overflow-hidden mb-4">
                  <img
                    src={event.image}
                    alt={event.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105 opacity-70"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0F0F0F] via-[#0F0F0F]/20 to-transparent" />
                </div>
                <h3 className="font-serif text-xl text-[#F4F2EE] mb-2">
                  {event.title}
                </h3>
                <p className="text-sm text-[#F4F2EE]/50">{event.description}</p>
              </div>
            ))}
          </div>

          {/* Filter Statement */}
          <div className="max-w-2xl mx-auto text-center border border-[#B8A369]/20 py-10 px-8" data-testid="filter-statement">
            <p className="text-[#F4F2EE]/70 italic leading-relaxed mb-4">
              "If you're looking for background noise, there are options.
            </p>
            <p className="text-[#F4F2EE]/70 italic leading-relaxed">
              If you're looking for atmosphere, you're in the right place."
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 px-6 lg:px-12 relative overflow-hidden" data-testid="cta-section">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1768508950719-4d76978fdf44?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwxfHxjb3Jwb3JhdGUlMjBnYWxhJTIwZXZlbnQlMjBlbGVnYW50JTIwYXRtb3NwaGVyZXxlbnwwfHx8fDE3NjkzMjU0NDN8MA&ixlib=rb-4.1.0&q=85"
            alt="Elegant event"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0F0F0F] via-[#0F0F0F]/90 to-[#0F0F0F]" />
        </div>

        <div className="relative z-10 max-w-3xl mx-auto text-center">
          <p className="text-xs tracking-[0.3em] uppercase text-[#B8A369] mb-6">
            Limited Availability
          </p>
          <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-[#F4F2EE] mb-6">
            Ready to Elevate Your Event
          </h2>
          <p className="text-[#F4F2EE]/60 leading-relaxed mb-10">
            Every engagement is thoughtfully considered. Submit an inquiry to begin the conversation.
          </p>
          <Link
            to="/inquiry"
            className="btn-luxury-outline"
            data-testid="cta-button"
          >
            Request Availability
          </Link>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
